import dev.shanty.kotwire.stimulus.Controller
import dev.shanty.kotwire.stimulus.StimulusController
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.get
import kotlin.collections.forEach

@JsExport
@StimulusController(targets = ["map", "point"], values = MapController.Values::class)
class MapController(context: dynamic) : Controller(context), MapControllerTargetLifecycleHooks {
    private val map by mapTarget<HTMLElement>()
    private val zoom by zoomValue()

    private val points by pointTargets<HTMLElement>()
    private var lMap: LeafletMap? = null
    private var markersLayerGroup: LeafletLayerGroup? = null

    override fun connect() {
        lMap = L.map(map.id)
        markersLayerGroup = L.layerGroup()

        // Add tile layer once during initialization
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", LeafletTileLayerOptions(
            attribution = "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a>"
        )).addTo(lMap!!)

        if(points.isEmpty()) {
            lMap!!.setView(L.latLng(51.508225918445184, -0.12805061748792648), zoom.toInt())
        } else {
            val firstPoint = points.first().let {
                ParsedPoint(
                    name = it.attributes["name"]?.value ?: "No Name",
                    description = it.innerText,
                    latitude = it.attributes["latitude"]?.value?.toDoubleOrNull() ?: 0.0,
                    longitude = it.attributes["longitude"]?.value?.toDoubleOrNull() ?: 0.0,
                )
            }

            lMap!!.setView(L.latLng(firstPoint.latitude, firstPoint.longitude), zoom.toInt())
        }

        updateMap()
        showCurrentLocation()
    }

    private fun showCurrentLocation() {
        val map = lMap ?: return
        val geo = window.navigator.asDynamic().geolocation ?: return
        geo.getCurrentPosition({ position: dynamic ->
            val lat = position.coords.latitude as Double
            val lng = position.coords.longitude as Double
            val marker = L.marker(L.latLng(lat, lng))
            marker.bindPopup("You are here")
            marker.addTo(map)
        })
    }

    override fun pointTargetConnected(element: HTMLElement) {
        updateMap()
    }

    override fun mapTargetDisconnected(element: HTMLElement) {
        updateMap()
    }

    fun updateMap() {
        val lMap = lMap ?: return
        val layerGroup = markersLayerGroup ?: return

        println("Map is not null")

        // Clear existing markers from the layer group
        layerGroup.clearLayers()

        if(points.isEmpty()) {
            println("Points is empty")
            return
        }

        val zoom = zoom.toInt()

        val parsedPoints = points.map {
            ParsedPoint(
                name = it.attributes.get("name")?.value ?: "No name",
                description = it.innerText,
                latitude = it.attributes.get("latitude")?.value?.toDoubleOrNull() ?: 0.0,
                longitude = it.attributes.get("longitude")?.value?.toDoubleOrNull() ?: 0.0,
            )
        }

        val firstPoint = parsedPoints.first()

        lMap.setView(L.latLng(firstPoint.latitude, firstPoint.longitude), zoom)

        // Add markers to the layer group instead of directly to the map
        parsedPoints.forEach {
            val marker = L.marker(L.latLng(it.latitude, it.longitude))
            val popText = if(it.description.isEmpty()) {
                it.name
            } else {
                """${it.name}
                    |
                    |${it.description}
                """.trimMargin()
            }
            marker.bindPopup(popText)
            layerGroup.addLayer(marker)
        }

        // Add the layer group to the map
        layerGroup.addTo(lMap)
    }

    // Helper functions for LayerGroup management
    fun showMarkers() {
        markersLayerGroup?.addTo(lMap!!)
    }

    fun hideMarkers() {
        markersLayerGroup?.removeFrom(lMap!!)
    }

    fun toggleMarkers() {
        val layerGroup = markersLayerGroup ?: return
        val lMap = lMap ?: return

        if (lMap.hasLayer(layerGroup)) {
            layerGroup.removeFrom(lMap)
        } else {
            layerGroup.addTo(lMap)
        }
    }

    object Values {
        val zoom: Int = 13
    }
}

data class ParsedPoint(
    val name: String,
    val description: String,
    val latitude: Double,
    val longitude: Double,
)

external object L {
    fun map(id: String, options: dynamic = definedExternally): LeafletMap
    fun tileLayer(url: String, options: dynamic = definedExternally): LeafletTileLayer
    fun marker(latLng: LeafletLatLng, options: dynamic = definedExternally): LeafletMarker
    fun latLng(lat: Double, lng: Double): LeafletLatLng
    fun circle(latLng: LeafletLatLng, options: CircleOptions = definedExternally): LeafletCircle
    fun layerGroup(layers: Array<LeafletLayer> = definedExternally): LeafletLayerGroup
    fun featureGroup(layers: Array<LeafletLayer> = definedExternally): LeafletFeatureGroup
    fun divIcon(options: dynamic = definedExternally): dynamic
}

external class LeafletMap {
    fun setView(center: LeafletLatLng, zoom: Int) : LeafletMap
    fun fitBounds(bounds: LeafletLatLngBounds, options: dynamic = definedExternally): LeafletMap
    fun hasLayer(layer: LeafletLayer): Boolean
}

external class LeafletTileLayer : LeafletLayer {
    fun addTo(map: LeafletMap) : LeafletTileLayer
}

external class LeafletMarker : LeafletLayer {
    fun addTo(map: LeafletMap) : LeafletMarker
    fun bindPopup(content: String) : LeafletPopup
}

external class LeafletPopup {
    fun openPopup() : LeafletPopup
}

external class LeafletCircle : LeafletLayer {
    fun addTo(map: LeafletMap) : LeafletCircle
}

open external class LeafletLayerGroup : LeafletLayer {
    fun addTo(map: LeafletMap): LeafletLayerGroup
    fun removeFrom(map: LeafletMap): LeafletLayerGroup
    fun addLayer(layer: LeafletLayer): LeafletLayerGroup
    fun removeLayer(layer: LeafletLayer): LeafletLayerGroup
    fun clearLayers(): LeafletLayerGroup
    fun hasLayer(layer: LeafletLayer): Boolean
    fun getLayers(): Array<LeafletLayer>
    fun eachLayer(fn: (LeafletLayer) -> Unit): LeafletLayerGroup
}

external class LeafletFeatureGroup : LeafletLayerGroup {
    fun getBounds(): LeafletLatLngBounds
}

// Base class for all layers
external interface LeafletLayer

external class LeafletLatLng

external class LeafletLatLngBounds

@JsExport
class LeafletTileLayerOptions(
    @JsName("attribution")
    val attribution: String? = null,
)

@JsExport
class CircleOptions(
    @JsName("radius")
    val radius: Double,
)